import { useMemo, useState, useContext, useCallback } from 'react';
import {
  makeStyles,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  IconButton,
} from '@material-ui/core';
import LibraryAddIcon from '@material-ui/icons/LibraryAdd';
import RestoreIcon from '@material-ui/icons/Restore';
import { SidebarContext, SidebarItem } from '@backstage/core-components';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { useHiddenSidebarItems } from './CustomSidebarItem';
import { SIDEBAR_ITEMS_CONFIG } from './sidebarConstants';
import { useFeatureFlag } from '@internal/plugin-feature-flags-common/components';
import {
  ADR_FEATURE_FLAG,
  GOLDEN_PATHS_FEATURE_FLAG,
  MY_AI_TOOLS_FEATURE_FLAG,
} from '@internal/plugin-feature-flags-common';

const useStyles = makeStyles(theme => ({
  menuPaper: {
    minWidth: 250,
    maxHeight: 400,
  },
  menuHeader: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  menuHeaderText: {
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  menuItem: {
    cursor: 'default',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  menuIcon: {
    minWidth: 36,
  },
  restoreIconButton: {
    padding: 6,
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.success.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  badge: {
    // Rendered in SidebarItem's secondary-action column (same slot as home icon).
    width: 18,
    height: 18,
    margin: '0 auto',
    backgroundColor: theme.palette.status.warning,
    color: theme.palette.warningText,
    borderRadius: '50%',
    fontSize: '0.7rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
  },
  sidebarItemWrapper: {
    position: 'relative',
  },
  collapsedLabel: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: '0.55rem',
    color: theme.palette.navigation.color,
    opacity: 0.7,
    lineHeight: 1,
    pointerEvents: 'none',
  },
  restoreAllMenuItem: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

export const HiddenItemsSidebarSection = () => {
  const classes = useStyles();
  const alertApi = useApi(alertApiRef);
  const { isOpen } = useContext(SidebarContext);
  const { hiddenItems, unhideItem, unhideAll } = useHiddenSidebarItems();
  const { enabled: isAdrEnabled } = useFeatureFlag(ADR_FEATURE_FLAG);
  const { enabled: isGoldenPathsEnabled } = useFeatureFlag(
    GOLDEN_PATHS_FEATURE_FLAG,
  );
  const { enabled: isMyAiToolsEnabled } = useFeatureFlag(
    MY_AI_TOOLS_FEATURE_FLAG,
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = useCallback((event: React.MouseEvent<Element>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget as HTMLElement);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleRestoreItem = useCallback(
    async (path: string) => {
      try {
        await unhideItem(path);
      } catch (error) {
        alertApi.post({
          message: 'Failed to restore sidebar item. Please try again.',
          severity: 'error',
          display: 'transient',
        });
      }
    },
    [unhideItem, alertApi],
  );

  const handleRestoreAll = useCallback(async () => {
    try {
      await unhideAll();
      handleClose();
    } catch (error) {
      alertApi.post({
        message: 'Failed to restore all items. Please try again.',
        severity: 'error',
        display: 'transient',
      });
    }
  }, [unhideAll, handleClose, alertApi]);

  // Get hidden items with their config
  const hiddenItemsWithConfig = useMemo(() => {
    return hiddenItems
      .map(path => {
        if (path === '/golden-paths' && !isGoldenPathsEnabled) {
          return null;
        }
        if (path === '/adr' && !isAdrEnabled) {
          return null;
        }
        if (path === '/my-ai-tools' && !isMyAiToolsEnabled) {
          return null;
        }
        const config = SIDEBAR_ITEMS_CONFIG[path];
        if (!config) return null;
        return { path, ...config };
      })
      .filter(Boolean)
      .sort((a, b) => a!.text.localeCompare(b!.text)) as Array<{
      path: string;
      icon: React.ComponentType<any>;
      text: string;
    }>;
  }, [hiddenItems, isGoldenPathsEnabled, isMyAiToolsEnabled, isAdrEnabled]);

  // Don't render if no restorable hidden items (after feature-flag filtering)
  if (hiddenItemsWithConfig.length === 0) {
    return null;
  }

  return (
    <div className={classes.sidebarItemWrapper}>
      <SidebarItem
        icon={LibraryAddIcon}
        text="Add Items"
        onClick={handleClick}
        aria-label={`Add sidebar items. ${hiddenItemsWithConfig.length} items hidden.`}
      >
        {isOpen ? (
          <span
            className={classes.badge}
            role="status"
            aria-live="polite"
            aria-label={`${hiddenItemsWithConfig.length} items hidden`}
          >
            {hiddenItemsWithConfig.length}
          </span>
        ) : null}
      </SidebarItem>
      {/* Show label below icon when sidebar is collapsed */}
      {!isOpen && <span className={classes.collapsedLabel}>Add Items</span>}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ className: classes.menuPaper }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        getContentAnchorEl={null}
      >
        {/* Header */}
        <Box className={classes.menuHeader}>
          <Typography className={classes.menuHeaderText} component="h3">
            Add Sidebar Items
          </Typography>
        </Box>

        {/* Items List */}
        {[
          ...hiddenItemsWithConfig.map(item => {
            const IconComponent = item.icon;
            return (
              <MenuItem
                key={item.path}
                className={classes.menuItem}
                disableRipple
              >
                <ListItemIcon className={classes.menuIcon}>
                  <IconComponent fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item.text} />
                <IconButton
                  size="small"
                  className={classes.restoreIconButton}
                  onClick={e => {
                    e.stopPropagation();
                    handleRestoreItem(item.path);
                  }}
                  title="Restore to sidebar"
                  aria-label={`Restore ${item.text} to sidebar`}
                >
                  <LibraryAddIcon fontSize="small" />
                </IconButton>
              </MenuItem>
            );
          }),
          <MenuItem
            key="restore-all"
            onClick={handleRestoreAll}
            className={classes.restoreAllMenuItem}
          >
            <ListItemIcon className={classes.menuIcon}>
              <RestoreIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Restore All Items"
              primaryTypographyProps={{ style: { fontWeight: 600 } }}
            />
          </MenuItem>,
        ]}
      </Menu>
    </div>
  );
};
